import styles from './FirstTourproSignup.module.scss' // 스타일 분리
import React from 'react'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import BasicField from '../components/BasicField/BasicField'
import SignupInfos from '../components/BasicField/SignupInfos'
import ValidationMap from '../components/BasicField/ValidationMap'
import { CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { useSponsors, useProAgencies } from '../../../../hooks'
import { useStateMachine } from 'little-state-machine'
import { updateStore } from '../../../../store'

// 약관 PDF 링크 (CDN)
const TERMS_CDN_BASE = 'https://dumzpdd1jp1uu.cloudfront.net'
const TERMS_LINK_PRIVACY = `${TERMS_CDN_BASE}/%E1%84%80%E1%85%A2%E1%84%8B%E1%85%B5%E1%86%AB%E1%84%8C%E1%85%A5%E1%86%BC%E1%84%87%E1%85%A9%E1%84%8E%E1%85%A5%E1%84%85%E1%85%B5%E1%84%87%E1%85%A1%E1%86%BC%E1%84%8E%E1%85%B5%E1%86%B7_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`
const TERMS_LINK_SERVICE = `${TERMS_CDN_BASE}/%E1%84%89%E1%85%A5%E1%84%87%E1%85%B5%E1%84%89%E1%85%B3%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%AD%E1%86%BC%E1%84%8B%E1%85%A3%E1%86%A8%E1%84%80%E1%85%AA%E1%86%AB_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`
const TERMS_LINK_ELECTRONIC = `${TERMS_CDN_BASE}/%E1%84%8C%E1%85%A5%E1%86%AB%E1%84%8C%E1%85%A1%E1%84%80%E1%85%B3%E1%86%B7%E1%84%8B%E1%85%B2%E1%86%BC%E1%84%80%E1%85%A5%E1%84%85%E1%85%A2%E1%84%83%E1%85%A9%E1%86%BC%E1%84%8B%E1%85%B4%E1%84%89%E1%85%A5_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`
const TERMS_LINK_MARKETING = `${TERMS_CDN_BASE}/%E1%84%86%E1%85%A1%E1%84%8F%E1%85%A6%E1%84%90%E1%85%B5%E1%86%BC%E1%84%89%E1%85%AE%E1%84%89%E1%85%B5%E1%86%AB%E1%84%83%E1%85%A9%E1%86%BC%E1%84%8B%E1%85%B4_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`

const FirstTourproSignup = () => {
	const navigate = useNavigate()
	const methods = useForm()
	const [emailCheckResult, setEmailCheckResult] = React.useState(null)

	// Store
	const { actions, state } = useStateMachine({ updateStore })

	// API 훅들 - 모든 스폰서/에이젼시 불러오기
	const { data: sponsors, isLoading: sponsorsLoading } = useSponsors()
	const { data: agencies, isLoading: agenciesLoading } = useProAgencies()

	const handleNext = async (data) => {
		// 중복 확인 결과가 'available'이 아니면 에러 띄우고 중단
		if (emailCheckResult !== 'available') {
			methods.setError('email', {
				type: 'manual',
				message: '이메일 중복 확인이 필요합니다.',
			})
			setEmailCheckResult(false)
			return
		}

		console.log('제출된 데이터:', data)

		// Store에 데이터 저장
		actions.updateStore({
			signupInput: {
				...state.signupInput,
				email: data.email,
				password: data.password,
				lastname: data.lastname,
				firstname: data.firstname,
				proCareer: data.proCareer ? data.proCareer.value : '',
				mainSponsor: data.mainSponsor ? data.mainSponsor.value : '',
				agency: data.agency ? data.agency.value : '',
				businessLicense: data.businessLicense,
				// 서비스 동의 관련
				protection: data.protection,
				useTerms: data.useTerms,
				message: data.message,
				cash: data.cash,
				fullTerms: data.fullTerms,
			},
		})

		navigate('/signup/tour-pro/verification')
	}

	const handleCheck = (target) => {
		const current = methods.getValues(target)
		methods.setValue(target, !current)
		methods.setValue('fullTerms', false)
	}
	const handleAllCheck = () => {
		if (watchedTerms.some((val) => val === true)) {
			methods.setValue(`protection`, false)
			methods.setValue(`useTerms`, false)
			methods.setValue(`message`, false)
			methods.setValue(`cash`, false)
		} else {
			methods.setValue(`protection`, true)
			methods.setValue(`useTerms`, true)
			methods.setValue(`message`, true)
			methods.setValue(`cash`, true)
		}
	}
	const watchedTerms = useWatch({
		control: methods.control,
		name: ['protection', 'useTerms', 'message', 'cash'],
	})

	const requiredFields = SignupInfos.filter(
		(info) => ValidationMap[info.validationType]?.required,
	).map((info) => info.name)

	const allRequiredFields = [...requiredFields, 'protection', 'useTerms', 'cash']

	const watchedAll = useWatch({
		control: methods.control,
		name: allRequiredFields,
	})

	// 동적으로 업데이트된 SignupInfos 생성
	const getUpdatedSignupInfos = () => {
		return SignupInfos.map((info) => {
			if (info.name === 'mainSponsor') {
				return {
					...info,
					options: sponsorsLoading
						? ['로딩 중...']
						: sponsors || ['스폰서를 불러올 수 없습니다'],
				}
			}
			if (info.name === 'agency') {
				return {
					...info,
					options: agenciesLoading
						? ['로딩 중...']
						: agencies || ['에이젼시를 불러올 수 없습니다'],
				}
			}
			if (info.name === 'proCareer') {
				// 경력 옵션은 이미 객체 형태로 정의되어 있으므로 그대로 사용
				return info
			}
			return info
		})
	}

	const updatedSignupInfos = getUpdatedSignupInfos()
	return (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit(handleNext)}>
				<div className={styles.signupWrapper}>
					<div className={styles.strongTitle}>투어프로 회원가입</div>
					<div className={styles.signupBox}>
						{updatedSignupInfos.map((info) => {
							const Component = info.component || BasicField
							return (
								methods.register && (
									<Component
										name={info.name}
										{...info}
										validation={ValidationMap[info.validationType]}
										emailCheckResult={emailCheckResult}
										setEmailCheckResult={setEmailCheckResult}
									/>
								)
							)
						})}

						{/* <BasicField label={'이메일'} validation={ValidationMap['email']} /> */}
						<div>
							<div>
								<input
									type="checkbox"
									id="fullTerms"
									style={{ display: 'none' }}
									onClick={handleAllCheck}
								/>
								<label htmlFor="fullTerms" className={styles.fullTerms}>
									전체 동의
									<span className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '24px',
												height: '24px',
												color: watchedTerms.every(Boolean)
													? '#1B1B1B'
													: '#ECECEC',
											}}
										/>
									</span>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="protection"
									{...methods.register('protection', {
										required: '필수 약관입니다.',
									})}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`protection`)}
								/>
								<label htmlFor="protection" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>개인정보 처리 방침 </small>
										<a
											href={TERMS_LINK_PRIVACY}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<span className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('protection')
													? '#1B1B1B'
													: 'ECECEC',
											}}
										/>
									</span>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="useTerms"
									{...methods.register('useTerms', {
										required: '필수 약관입니다.',
									})}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`useTerms`)}
								/>
								<label htmlFor="useTerms" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>서비스 이용약관 </small>
										<a
											href={TERMS_LINK_SERVICE}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<span className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('useTerms')
													? '#1B1B1B'
													: 'ECECEC',
											}}
										/>
									</span>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="cash"
									{...methods.register('cash', { required: '필수 약관입니다.' })}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`cash`)}
								/>
								<label htmlFor="cash" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>전자금융거래 이용 동의서</small>
										<a
											href={TERMS_LINK_ELECTRONIC}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<small className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('cash') ? '#1B1B1B' : 'ECECEC',
											}}
										/>
									</small>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="message"
									{...methods.register('message')}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`message`)}
								/>
								<label htmlFor="message" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>
											서비스 알림 수신 동의
											<span className="choice"> (선택) </span>
										</small>
										<a
											href={TERMS_LINK_MARKETING}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<small className={styles.right}>
										<CheckCircleIcon
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('message')
													? '#1B1B1B'
													: 'ECECEC',
											}}
										/>
									</small>
								</label>
							</div>
						</div>
						{/*필수 폼이 모두 작성되지 않았으면 비활성화*/}
						<button
							className={styles.nextButton}
							type="submit"
							style={{
								backgroundColor: watchedAll.every(Boolean) ? '#1b1b1b' : '#e4e4e4',
							}}
							disabled={!watchedAll.every(Boolean)}>
							다음
						</button>
					</div>
				</div>
			</form>
		</FormProvider>
	)
}

export { FirstTourproSignup }
